<script>
export default {
  props: {
    item: {
      type: Object,
      required: true
    },
    uuidCopyStatus: {
      type: String,
      default: 'idle'
    }
  },
  computed: {
    row () {
      return this.item || {}
    },
    files () {
      return Array.isArray(this.row.files) ? this.row.files : []
    },
    untrackedReadCount () {
      const total = Number(this.row.consultations_count)
      const documentReads = Number.isFinite(total) && total >= 0 ? total : 0
      const trackedReads = this.files.reduce((sum, file) => sum + this.fileReadCount(file), 0)
      return Math.max(documentReads - trackedReads, 0)
    },
    untrackedReadMessage () {
      const count = this.untrackedReadCount
      const noun = `lecture${count === 1 ? '' : 's'}`
      return `${count} ${noun} ne ${count === 1 ? 'peut' : 'peuvent'} pas être rattachée${count === 1 ? '' : 's'} à une pièce jointe et ne ${count === 1 ? 'peut' : 'peuvent'} donc pas être détaillée${count === 1 ? '' : 's'}.`
    },
    groupLabels () {
      const notifications = Array.isArray(this.row.notifications) ? this.row.notifications : []
      return [...new Set(notifications
        .map(notification => notification && notification.group && notification.group.label)
        .filter(Boolean))]
    },
    truncatedUuid () {
      const value = this.row.uuid ? String(this.row.uuid) : ''
      return value.length > 16 ? `${value.slice(0, 14)}…` : value
    },
    uuidCopyIcon () {
      if (this.uuidCopyStatus === 'copied') return 'fa-check'
      if (this.uuidCopyStatus === 'error') return 'fa-exclamation-circle'
      return 'fa-copy'
    },
    uuidCopyMessage () {
      if (this.uuidCopyStatus === 'copied') return 'Copié'
      if (this.uuidCopyStatus === 'error') return 'Copie impossible'
      return ''
    }
  },
  methods: {
    fileReadCount (file) {
      const count = Number(file && file.consultations_count)
      return Number.isFinite(count) && count >= 0 ? count : 0
    },
    fileReadLabel (file) {
      const count = this.fileReadCount(file)
      return `${count} lecture${count === 1 ? '' : 's'}`
    }
  },
  filters: {
    fileSize: function (num) {
      var base = 1024
      var prefixes = ['k', 'M', 'G', 'T']
      var exp = Math.log(num) / Math.log(base) | 0
      return (num / Math.pow(base, exp)).toFixed(1) + ' ' + ((exp > 0) ? prefixes[ exp - 1 ] + 'o' : 'Bytes')
    }
  }
}
</script>

<template>
  <div class="document-preview space-y-2 py-4 whitespace-normal">
    <div v-if="row.uuid" class="document-preview__uuid-row">
      <span class="document-preview__eyebrow">Aperçu du document</span>
      <div class="document-preview__uuid-group">
        <button
          type="button"
          class="document-preview__uuid"
          :class="{
            'document-preview__uuid--copied': uuidCopyStatus === 'copied',
            'document-preview__uuid--error': uuidCopyStatus === 'error'
          }"
          :title="`Copier l'UUID complet : ${row.uuid}`"
          :aria-label="`Copier l'UUID complet ${row.uuid}`"
          @click="$emit('copy-uuid', row.uuid)"
        >
          <span>UUID: {{ truncatedUuid }}</span>
          <i class="fas" :class="uuidCopyIcon" aria-hidden="true"></i>
        </button>
        <span
          class="document-preview__uuid-feedback"
          :class="{ 'document-preview__uuid-feedback--error': uuidCopyStatus === 'error' }"
          role="status"
          aria-live="polite"
        >{{ uuidCopyMessage }}</span>
      </div>
    </div>
    <div class="document-preview__row">
      <div class="document-preview__label">Titre</div>
      <div class="document-preview__value">{{ row.title || '—' }}</div>
    </div>
    <div class="document-preview__row">
      <div class="document-preview__label">Description</div>
      <div v-if="row.description" class="document-preview__value" v-html="row.description" />
      <div v-else class="document-preview__value">—</div>
    </div>
    <div class="document-preview__row">
      <div class="document-preview__label">Pièces jointes</div>
      <div class="document-preview__value">
        <ul v-if="files.length" class="document-preview__files">
          <li v-for="(file, i) in files" :key="file.uuid || i">
            <a :href="file.url" target="_blank" rel="noopener noreferrer">
              <i class="far fa-eye" aria-hidden="true"></i>
              <span>{{ file.name }} ({{ file.size | fileSize }})</span>
            </a>
            <span v-if="files.length > 1" class="document-preview__file-reads">
              — {{ fileReadLabel(file) }}
            </span>
          </li>
        </ul>
        <span v-else>—</span>
        <p v-if="untrackedReadCount > 0" class="document-preview__untracked-reads">
          <i class="fas fa-info-circle" aria-hidden="true"></i>
          <span>{{ untrackedReadMessage }}</span>
        </p>
      </div>
    </div>
    <div v-if="groupLabels.length" class="document-preview__row">
      <div class="document-preview__label">Groupes</div>
      <div class="document-preview__value">{{ groupLabels.join(', ') }}</div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.document-preview {
  color: #374151;
}

.document-preview__uuid-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
}

.document-preview__eyebrow {
  color: #2563eb;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.document-preview__uuid-group {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.document-preview__uuid {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 9px;
  color: #1d4ed8;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  cursor: pointer;
}

.document-preview__uuid:hover,
.document-preview__uuid:focus-visible {
  background: rgba(59, 130, 246, 0.2);
  border-color: rgba(59, 130, 246, 0.5);
}

.document-preview__uuid:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.document-preview__uuid--copied {
  color: #15803d;
  background: rgba(34, 197, 94, 0.1);
  border-color: rgba(34, 197, 94, 0.3);
}

.document-preview__uuid--error {
  color: #b91c1c;
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
}

.document-preview__uuid-feedback {
  color: #15803d;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.document-preview__uuid-feedback--error {
  color: #b91c1c;
}

.document-preview__row {
  display: grid;
  grid-template-columns: minmax(120px, 1fr) minmax(0, 5fr);
  gap: 16px;
}

.document-preview__label {
  font-weight: 700;
  text-align: right;
}

.document-preview__value {
  min-width: 0;
  overflow-wrap: anywhere;
}

.document-preview__files {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.document-preview__files li {
  display: flex;
  align-items: center;
  gap: 10px;
}

.document-preview__files a {
  display: inline-flex;
  align-items: center;
  flex: 1;
  gap: 7px;
  min-width: 0;
  color: #2563eb;
}

.document-preview__file-reads {
  color: #6b7280;
  flex: none;
  font-size: 12px;
  white-space: nowrap;
}

.document-preview__untracked-reads {
  align-items: flex-start;
  color: #6b7280;
  display: flex;
  font-size: 12px;
  gap: 6px;
  margin-top: 8px;
}

.document-preview__untracked-reads i {
  color: #9ca3af;
  margin-top: 2px;
}

@media (max-width: 640px) {
  .document-preview__row {
    grid-template-columns: 1fr;
    gap: 2px;
  }

  .document-preview__label {
    text-align: left;
  }
}
</style>
